"use client";

import { useEffect, useState } from "react";
import {
  Home,
  ShoppingCart,
  User,
  MessageCircle,
  Filter,
  X,
} from "lucide-react";
import { Button } from "../ui/button";
import Link from "next/link";
import { useParams, usePathname } from "next/navigation";
import { fetchCategoriesBySlug } from "@/api-service/category";
import { Category } from "@/packages/types/category";
import Sidebar from "@/components/gridPage/sidebar/SideBar";
import { toast } from "sonner";
import { BrandRelatedRespone } from "@/packages/types/brands";
import { fetchBrandBySlug } from "@/api-service/brands";
import { useAuth } from "@/packages/context/AuthContext";
import { useCart } from "@/packages/context/CartContext";

const HomeBar = () => {
  const { cartItems } = useCart();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const params = useParams();
  const slug = params?.slug as string;
  const pathname = usePathname();

  const [categoryData, setCategoryData] = useState<Category>();
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const { isLoggedIn } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);
  const hideFilterOn = [
    "/cart",
    "/user-profile",
    "pages",
    "/products/",
    "/search-grid/",
    "/brand/",
    "/profile",
    "/checkout",
    "/sign-in",
    "/sign-up",
  ];

  function toRootCategory(response: BrandRelatedRespone): Category {
    return {
      data: {
        id: response.data.id,
        name: "root",
        slug: "root",
        parentId: null,
        galleryImages: [],
        galleryImageUrls: [],
        children: response.data.category.map((c) => ({
          id: c.id,
          name: c.name,
          slug: c.slug,
          parentId: null,
          galleryImages: [],
          galleryImageUrls: [],
          children: [],
          minPrice: response.data.minPrice,
          maxPrice: response.data.maxPrice,
        })),
        minPrice: response.data.minPrice,
        maxPrice: response.data.maxPrice,
      },
    };
  }

  // Fetch category or brand data based on path
  useEffect(() => {
    if (!slug) return;

    if (
      pathname.startsWith("/pages") ||
      pathname.startsWith("/products/")
    ) {
      return;
    }

    const fetchData = async () => {
      try {
        if (pathname.includes("brand") || pathname.includes("vendor")) {
          // Brand API call
          const res = await fetchBrandBySlug(slug);
          const formattedResponse = toRootCategory(res);
          setCategoryData(formattedResponse);

          if (res?.data?.minPrice) setMinPrice(res.data.minPrice);
          if (res?.data?.maxPrice) setMaxPrice(res.data.maxPrice);
        } else {
          // Category API call
          const res = await fetchCategoriesBySlug(slug);
          setCategoryData(res);

          if (res?.data?.minPrice) setMinPrice(res.data.minPrice);
          if (res?.data?.maxPrice) setMaxPrice(res.data.maxPrice);
        }
      } catch (error: unknown) {
        console.error("Error fetching data:", error);
        const errorData = (error as Record<string, unknown>)?.data as
          | Record<string, unknown>
          | undefined;
        const errorMsg = (
          errorData?.data as Record<string, unknown> | undefined
        )?.message as string | undefined;
        toast.error(errorMsg || "Something went wrong");
      }
    };

    fetchData();
  }, [slug, pathname]);



  if (
    pathname.startsWith("/cart") ||
    pathname.startsWith("/products/") ||
    pathname.startsWith("/product-detail/")
  ) {
    return null;
  }

  return (
    <>
      {/* Bottom Navigation Bar */}
      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-md flex justify-around items-center py-2 md:hidden"
      >
        <Link
          href={"/"}
          className="flex flex-col items-center text-xs text-gray-700"
        >
          <Home className="w-5 h-5 mb-1" />
          Home
        </Link>

        {/* Show Filter only if not in excluded pages */}
        {/* Show Filter only if not in excluded pages */}
        {!(
          pathname === "/" ||
          hideFilterOn.some((path) => pathname.startsWith(path))
        ) && (
          <Button
            onClick={() => setSidebarOpen(true)}
            className="flex flex-col items-center text-xs text-gray-700"
          >
            <Filter className="w-5 h-5 mb-1" />
            Filter
          </Button>
        )}

        <Link
          href={"/cart"}
          className="flex flex-col items-center text-xs text-gray-700 relative"
        >
          <div className="relative">
            <ShoppingCart className="w-5 h-5 mb-1" />
            {mounted && cartItems.reduce((acc, item) => acc + item.quantity, 0) > 0 && (
              <span className="absolute -top-1.5 -right-2 inline-flex h-4 w-4 items-center justify-center rounded-full bg-gradient-to-br from-rose-500 to-rose-600 text-[9px] font-bold text-white shadow ring-1 ring-white">
                {cartItems.reduce((acc, item) => acc + item.quantity, 0)}
              </span>
            )}
          </div>
          Cart
        </Link>

        <Link
          href={mounted && isLoggedIn ? "/user-profile" : "/sign-in"}
          className="flex flex-col items-center text-xs text-gray-700"
        >
          <User className="w-5 h-5 mb-1" />
          Profile
        </Link>

        <Button className="flex flex-col items-center text-xs text-gray-700">
          <MessageCircle className="w-5 h-5 mb-1" />
          Chat
        </Button>
      </div>

      {/* Sidebar Drawer for Mobile */}
      {!hideFilterOn.includes(pathname) && (
        <div
          className={`fixed inset-0 z-50 transition ${
            sidebarOpen ? "visible" : "invisible"
          }`}
        >
          {/* Overlay */}
          <div
            className={`absolute inset-0 bg-black/40 transition-opacity ${
              sidebarOpen ? "opacity-100" : "opacity-0"
            }`}
            onClick={() => setSidebarOpen(false)}
          />

          {/* Drawer */}
          <div
            className={`absolute top-0 left-0 h-full w-4/5 bg-white shadow-lg transform transition-transform duration-300
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b">
              <h2 className="text-lg font-semibold">Filter</h2>
              <button onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Sidebar content */}
            <div className="p-4">
              <Sidebar
                category={categoryData}
                minPrice={minPrice}
                maxPrice={maxPrice}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HomeBar;
